import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { ResultOverlay } from '../components/ResultOverlay';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mocktail-hub-6/artifacts/yiexn6qe_Simple%20Modern%20Minimalist%20Circle%20Design%20Studio%20Logo.png";

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [overlay, setOverlay] = useState({ show: false, type: 'success', title: '', subtitle: '' });
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      login(data.token, data.user);
      setOverlay({ show: true, type: 'party', title: `Bienvenue ${data.user.name.split(' ')[0]} !`, subtitle: 'Connexion réussie' });
      setTimeout(() => navigate(data.user.role === 'admin' ? '/admin' : '/mon-espace', { replace: true }), 2000);
    } catch (err) {
      setOverlay({ show: true, type: 'error', title: 'Erreur de connexion', subtitle: err.response?.data?.detail || 'Email ou mot de passe incorrect' });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { toast.error('Entrez votre email'); return; }
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail, origin: window.location.origin });
      setForgotSent(true);
    } catch {
      toast.error('Erreur');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <ResultOverlay {...overlay} onClose={() => setOverlay(p => ({ ...p, show: false }))} />
      <div className="w-full max-w-sm">
        <div className="text-center mb-8 slide-up">
          <Link to="/">
            <img src={LOGO_URL} alt="RSX Concept" className="w-16 h-16 rounded-full mx-auto mb-4" />
          </Link>
          <h1 className="text-2xl font-light tracking-tight" style={{ fontFamily: 'Outfit' }}>Connexion</h1>
          <p className="text-sm text-[#A1A1AA] mt-1">{"Accédez à votre espace RSX Concept"}</p>
        </div>

        {showForgot ? (
          <div className="slide-up stagger-1 space-y-4">
            {forgotSent ? (
              <div className="bg-[#121212] border border-[#10B981]/20 rounded-2xl p-8 text-center">
                <Mail className="w-12 h-12 text-[#10B981] mx-auto mb-3" />
                <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>Email envoyé !</h3>
                <p className="text-sm text-[#A1A1AA] mb-4">{"Si un compte existe avec cet email, vous recevrez un lien de réinitialisation."}</p>
                <Button onClick={() => { setShowForgot(false); setForgotSent(false); }} variant="outline" className="border-white/10 rounded-full px-6 text-sm">Retour à la connexion</Button>
              </div>
            ) : (
              <form onSubmit={handleForgot} className="space-y-4">
                <h3 className="font-semibold text-center" style={{ fontFamily: 'Outfit' }}>Mot de passe oublié</h3>
                <p className="text-sm text-[#A1A1AA] text-center">{"Entrez votre email, nous vous enverrons un lien de réinitialisation."}</p>
                <div>
                  <Label className="text-xs text-[#A1A1AA] mb-1.5 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
                    <Input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required
                      data-testid="forgot-email" placeholder="vous@email.com"
                      className="pl-10 bg-[#121212] border-white/10 focus:border-[#D4AF37] h-11 rounded-xl text-sm" />
                  </div>
                </div>
                <Button type="submit" data-testid="forgot-submit" className="w-full bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full h-11 text-sm font-semibold">
                  Envoyer le lien
                </Button>
                <button type="button" onClick={() => setShowForgot(false)} className="w-full text-sm text-[#A1A1AA] hover:text-white">Retour</button>
              </form>
            )}
          </div>
        ) : (
          <div className="space-y-4 slide-up stagger-1">
            <Button onClick={handleGoogle} variant="outline" data-testid="google-login-btn"
              className="w-full border-white/10 bg-white/5 hover:bg-white/10 text-white rounded-xl h-11 text-sm">
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Continuer avec Google
            </Button>

            <div className="flex items-center gap-3 my-4">
              <div className="h-px bg-white/10 flex-1" />
              <span className="text-xs text-[#A1A1AA]">ou</span>
              <div className="h-px bg-white/10 flex-1" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs text-[#A1A1AA] mb-1.5 block">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                    data-testid="login-email" placeholder="vous@email.com"
                    className="pl-10 bg-[#121212] border-white/10 focus:border-[#D4AF37] h-11 rounded-xl text-sm" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-xs text-[#A1A1AA]">Mot de passe</Label>
                  <button type="button" onClick={() => setShowForgot(true)} className="text-[10px] text-[#D4AF37] hover:text-[#F9E5C0]" data-testid="forgot-password-link">
                    {"Mot de passe oublié ?"}
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
                  <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                    data-testid="login-password" placeholder="Votre mot de passe"
                    className="pl-10 bg-[#121212] border-white/10 focus:border-[#D4AF37] h-11 rounded-xl text-sm" />
                </div>
              </div>
              <Button type="submit" disabled={loading} data-testid="login-submit-btn"
                className="w-full bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full h-11 text-sm font-semibold apple-press">
                {loading ? <div className="spin-gold w-5 h-5" /> : <><ArrowRight className="w-4 h-4 mr-2" /> Se connecter</>}
              </Button>
            </form>

            <p className="text-center text-sm text-[#A1A1AA]">
              {"Pas encore de compte ? "}
              <Link to="/register" className="text-[#D4AF37] hover:text-[#F9E5C0]" data-testid="register-link">S'inscrire</Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
